import { useState } from "react";
import { BookOpen } from "lucide-react";
import { getApi } from "../../api";
import { useAsyncResource } from "../../hooks/useAsyncResource";
import { notify } from "../../store/uiStore";
import { reportError } from "../../services/eventBus";
import { Button } from "../Button";
import { MarkdownText } from "../MarkdownText";
import { ErrorState, Skeleton } from "../ScreenStates";
import { TextArea } from "../fields";

interface UnderwritingRuleEditorProps {
  conditionId: number;
  conditionName: string;
  children?: never;
}

interface RuleFormProps {
  conditionId: number;
  conditionName: string;
  initialMarkdown: string;
  skynetRule: string | null;
  onSaved: () => void;
}

function RuleForm({ conditionId, conditionName, initialMarkdown, skynetRule, onSaved }: RuleFormProps) {
  const [markdown, setMarkdown] = useState(initialMarkdown);
  const [isSaving, setIsSaving] = useState(false);
  const isDirty = markdown !== initialMarkdown;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await getApi().updateCondition(conditionId, { underwritingRuleMarkdown: markdown.trim() || null });
      notify(markdown.trim() ? `Saved the underwriting rules for ${conditionName}. Underwriters see them on Select Rating.` : `Cleared the underwriting rules for ${conditionName}.`, "success");
      onSaved();
    } catch (error) {
      reportError(error, "UnderwritingRuleEditor.save");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div>
        <TextArea id="underwriting-rule-markdown" label="Underwriting rules (Markdown)" value={markdown} onChange={setMarkdown} rows={10} hint="Plain-language guidance for underwriters. Headings, bullet lists, bold, and links are supported. Shown read-only on Select Rating." />
        <div className="mt-2 flex justify-end">
          <Button size="sm" onClick={() => void handleSave()} disabled={!isDirty} isLoading={isSaving}>
            Save rules
          </Button>
        </div>
      </div>
      <div className="space-y-4">
        <div className="rounded-lg border border-gray-200 p-3">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Preview</p>
          {markdown.trim() ? <MarkdownText text={markdown} /> : <p className="text-sm text-gray-400">Nothing written yet.</p>}
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Condition-Specific Rule (Skynet, read-only)</p>
          <p className="text-sm text-gray-700">{skynetRule ?? "None recorded. Skynet rules are maintained in Skynet Replay."}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Plain-language underwriting rules for one condition, written in Markdown by the underwriting or actuarial
 * team. Sits on the condition editor; underwriters read the result on Select Rating next to the
 * Condition-Specific Rule Skynet follows (which stays read-only here, since Skynet Replay owns it).
 */
export function UnderwritingRuleEditor({ conditionId, conditionName }: UnderwritingRuleEditorProps) {
  const guidance = useAsyncResource(() => getApi().getConditionGuidance(conditionId), String(conditionId), "UnderwritingRuleEditor.load");

  return (
    <section aria-label="Underwriting rules" className="rounded-xl border border-gray-200 p-4">
      <h2 className="mb-3 flex items-center gap-2 text-[15px] font-semibold text-brand-navy">
        <BookOpen size={18} aria-hidden="true" />
        Underwriting rules
      </h2>
      {guidance.state.kind === "error" && <ErrorState message={guidance.state.message} onRetry={guidance.reload} />}
      {guidance.state.kind === "loading" && !guidance.data && (
        <div className="space-y-2" role="status" aria-label="Loading underwriting rules">
          <Skeleton className="h-24 w-full" />
        </div>
      )}
      {guidance.data && <RuleForm conditionId={conditionId} conditionName={conditionName} initialMarkdown={guidance.data.underwritingRule ?? ""} skynetRule={guidance.data.skynetRule} onSaved={guidance.reload} />}
    </section>
  );
}
